import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import Sidebar from '../components/Sidebar';
import axios from 'axios';
import common from '../utils/common';
import { authStorage } from '../utils/authStorage';
import { fetchParticipants } from '../features/api/apiSlice';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import './ntc-branding.css';

const UnreleasedProjects = () => {
  const dispatch = useAppDispatch();
  const { participants: reduxParticipants } = useAppSelector((state) => state.api);
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [projects, setProjects] = useState([]);
  const [originalProjects, setOriginalProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [commitments, setCommitments] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loginData, setLoginData] = useState(null);
  const [addVesting, setAddVesting] = useState(false);
  const [vestingForm, setVestingForm] = useState({});
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [projectModalMode, setProjectModalMode] = useState('view');
  const [editingProject, setEditingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [projectForm, setProjectForm] = useState({
    projectName: '',
    projectURL: '',
    whitepaperURL: '',
    allocation: '',
    allocation_currency: 'USDT',
    tokenomics: '',
    dealanalysis: '',
    fund_wallet: '',
    timer: '24',
    telegram: '',
    poc_email: '',
    whatsapp: '',
    referred: false,
    referred_by: 0
  });

  // Custom Alert State
  const [customAlert, setCustomAlert] = useState({
    show: false,
    message: '',
    type: 'error'
  });

  const showAlert = (message, type = 'error') => {
    setCustomAlert({ show: true, message, type });
    setTimeout(() => {
      setCustomAlert({ show: false, message: '', type: 'error' });
    }, type === 'success' ? 4000 : 5000);
  };

  const hideAlert = () => {
    setCustomAlert({ show: false, message: '', type: 'error' });
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
    document.documentElement.classList.toggle('minimenu');
    localStorage.setItem('nexel-classic-dashboard-menu-mini-theme', !sidebarCollapsed ? 'menu-mini-theme' : '');
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('nexel-classic-dashboard-menu-mini-theme');
    if (savedTheme === 'menu-mini-theme') {
      setSidebarCollapsed(true);
      document.documentElement.classList.add('minimenu');
    } else {
      setSidebarCollapsed(false);
      document.documentElement.classList.remove('minimenu');
    }
  }, []);

  useEffect(() => {
    const data = authStorage.getLoginData();
    if (!data) return;
    if (authStorage.getDirectLink() === '1') {
      window.location.href = '/';
      return;
    }
    setLoginData(data);
  }, []);

  useEffect(() => {
    dispatch(fetchParticipants());
  }, [dispatch]);

  useEffect(() => {
    if (reduxParticipants) {
      setParticipants(reduxParticipants);
    }
  }, [reduxParticipants]);

  useEffect(() => {
    if (!loginData) return;
    
    const baseUrl = common.Data.API_URL;
    const url = window.location.href;
    const projectNameParam = url.split('?')[1];
    
    axios.get(`${baseUrl}/v1/get-projects/unreleased-${loginData.id}-${loginData.role}`)
      .then((response) => {
        const projectsData = response.data || [];
        setProjects(projectsData);
        setOriginalProjects(projectsData);
        
        if (projectsData.length > 0) {
          let selected = projectsData[0];
          if (projectNameParam) {
            const found = projectsData.find(p => p.name.toLowerCase() === decodeURIComponent(projectNameParam).toLowerCase());
            if (found) selected = found;
          }
          populateCommitment(selected);
        }
      })
      .catch((error) => {
        console.error('Error fetching projects:', error);
        showAlert('Failed to fetch projects', 'error');
      });
  }, [loginData]);

  // Filter projects based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setProjects(originalProjects);
      return;
    }
    
    const filtered = originalProjects.filter(project => 
      project.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setProjects(filtered);
    
    // If selected project is filtered out, select first filtered project
    if (filtered.length > 0 && selectedProject && !filtered.find(p => p.id === selectedProject.id)) {
      const firstFiltered = filtered[0];
      populateCommitment(firstFiltered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, originalProjects]);

  const populateCommitment = (project, onComplete) => {
    if (!project) {
      if (onComplete) onComplete();
      return;
    }
    
    const baseUrl = common.Data.API_URL;
    axios.get(`${baseUrl}/v1/get-commitment/${project.id}`)
      .then((response) => {
        setCommitments(response.data || []);
        setSelectedProject(project);
        populateVesting(project, onComplete);
      })
      .catch((error) => {
        console.error('Error fetching commitments:', error);
        showAlert('Failed to fetch commitment details', 'error');
        if (onComplete) onComplete();
      });
  };

  const populateVesting = (project, onComplete) => {
    if (!project) {
      if (onComplete) onComplete();
      return;
    }
    
    const baseUrl = common.Data.API_URL;
    axios.get(`${baseUrl}/v1/get-vesting/${project.id}`)
      .then((response) => {
        if (response.data && response.data.length > 0) {
          const vestingData = response.data[0];
          const tgeDate = new Date(vestingData.tge_date);
          setVestingForm({
            ...vestingData,
            tge_date: tgeDate.toISOString().slice(0, 16)
          });
        } else {
          setVestingForm({});
        }
        if (onComplete) onComplete();
      })
      .catch((error) => {
        console.error('Error fetching vesting:', error);
        if (onComplete) onComplete();
      });
  };

  const approveTx = (verified, id) => {
    const baseUrl = common.Data.API_URL;
    axios.get(`${baseUrl}/v1/trasaction-verification/${parseInt(id)}/${parseInt(verified)}`)
      .then(() => {
        showAlert(verified === 1 ? 'Transaction approved' : 'Transaction rejected', 'success');
        populateCommitment(selectedProject);
      })
      .catch((error) => {
        console.error('Error verifying transaction:', error);
        showAlert('Operation failed', 'error');
      });
  };

  const saveVesting = () => {
    if (!selectedProject || !loginData) return;
    
    const formData = {
      ...vestingForm,
      proj_id: selectedProject.id,
      created_by: loginData.id,
      project_round: vestingForm.project_round || 'Strategic',
      vesting_type: vestingForm.vesting_type || 'Monthly',
      tge_date: vestingForm.tge_date ? new Date(vestingForm.tge_date) : null
    };

    const totalPer = parseFloat(vestingForm.tge_release || 0) + 
                     parseInt(vestingForm.no_of_repitition || 0) * 
                     parseFloat(vestingForm.release_per_time || 0);

    if (!formData.price_per_token || !formData.no_of_repitition || 
        !formData.release_per_time || Math.round(totalPer) !== 100) {
      showAlert('Please fill all mandatory fields or check for percentage release mismatch', 'error');
      return;
    }

    const baseUrl = common.Data.API_URL;
    axios.post(`${baseUrl}/v1/add-vesting`, formData)
      .then(() => {
        showAlert('Vesting saved successfully', 'success');
        setTimeout(() => {
          window.location.href = `/unreleased?${selectedProject.name}`;
        }, 1500);
      })
      .catch((error) => {
        console.error('Error saving vesting:', error);
        showAlert('Failed to save vesting', 'error');
      });
  };

  const processLinksInHTML = (html) => {
    if (!html) return html;
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const links = tempDiv.querySelectorAll('a');
    links.forEach(link => {
      if (!link.getAttribute('target')) {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }
    });
    return tempDiv.innerHTML;
  };

  const handleViewProject = async (project) => {
    if (!project) return;
    
    setProjectModalMode('view');
    setEditingProject(project);
    setShowAddProjectModal(true);
    
    const processedTokenomics = processLinksInHTML(project.tokenomics || '');
    const processedDealAnalysis = processLinksInHTML(project.deal_analysis || '');
    
    setProjectForm({
      projectName: project.name || '',
      projectURL: project.url || '',
      whitepaperURL: project.whitepaper_url || '',
      allocation: project.allocation_available || '',
      allocation_currency: project.allocation_currency || 'USDT',
      tokenomics: processedTokenomics,
      dealanalysis: processedDealAnalysis,
      fund_wallet: project.fund_wallet_address || '',
      timer: project.timer || '24',
      telegram: project.telegram || '',
      poc_email: project.email || '',
      whatsapp: project.whatsapp || '',
      referred: project.status === 'referred',
      referred_by: project.referred_by || loginData?.id || 0
    });
  };

  const handleCloseAddProjectModal = () => {
    setShowAddProjectModal(false);
    setProjectModalMode('view');
    setEditingProject(null);
    setProjectForm({
      projectName: '',
      projectURL: '',
      whitepaperURL: '',
      allocation: '',
      allocation_currency: 'USDT',
      tokenomics: '',
      dealanalysis: '',
      fund_wallet: '',
      timer: '24',
      telegram: '',
      poc_email: '',
      whatsapp: '',
      referred: false,
      referred_by: loginData?.id || 0
    });
  };

  const calculateStats = () => {
    if (!selectedProject) {
      return {
        totalAllocation: 0,
        participants: 0,
        totalCommitment: 0,
        allocationAvailable: 0
      };
    }
    
    const totalAllocation = selectedProject.allocation_available ? parseInt(selectedProject.allocation_available) : 0;
    let totalCommitment = 0;
    
    commitments.forEach(commit => {
      totalCommitment += parseInt(commit.allocation_amount || 0);
    });
    
    const allocationAvailable = totalAllocation - totalCommitment;
    const participants = commitments.length;
    
    return {
      totalAllocation,
      participants,
      totalCommitment,
      allocationAvailable
    };
  };

  const stats = calculateStats();
  const isSpecialProject = selectedProject?.is_special && 
                          !(selectedProject.special_participants && selectedProject.special_participants.split(',').includes(loginData?.id + '')) && 
                          loginData?.role != 1;

  return (
    <div className="nxl-wrapper">
      {/* Custom Alert */}
      {customAlert.show && (
        <div 
          className="position-fixed"
          style={{
            top: '24px',
            right: '24px',
            zIndex: 9999,
            minWidth: '380px',
            maxWidth: '450px',
            animation: 'slideInRight 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
          }}
        >
          <div 
            className="card shadow-lg border-0"
            style={{
              borderRadius: '16px',
              overflow: 'hidden',
              border: `2px solid ${
                customAlert.type === 'error' ? '#dc3545' : 
                customAlert.type === 'success' ? '#28a745' : 
                customAlert.type === 'warning' ? '#ffc107' : 
                '#17a2b8'
              }`
            }}
          >
            <div 
              className={`card-body p-3 d-flex align-items-center position-relative`}
              style={{
                backgroundColor: '#ffffff',
                borderLeft: `5px solid ${
                  customAlert.type === 'error' ? '#dc3545' : 
                  customAlert.type === 'success' ? '#28a745' : 
                  customAlert.type === 'warning' ? '#ffc107' : 
                  '#17a2b8'
                }`
              }}
            >
              <div 
                className={`d-flex align-items-center justify-content-center me-3`}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: customAlert.type === 'error' ? 'rgba(220, 53, 69, 0.15)' : 
                                   customAlert.type === 'success' ? 'rgba(40, 167, 69, 0.15)' : 
                                   customAlert.type === 'warning' ? 'rgba(255, 193, 7, 0.15)' : 
                                   'rgba(23, 162, 184, 0.15)',
                  flexShrink: 0,
                  boxShadow: `0 2px 8px rgba(0,0,0,0.08)`
                }}
              >
                <i 
                  className={`feather-${
                    customAlert.type === 'error' ? 'alert-circle' : 
                    customAlert.type === 'success' ? 'check-circle' : 
                    customAlert.type === 'warning' ? 'alert-triangle' : 
                    'info'
                  }`}
                  style={{ 
                    fontSize: '24px', 
                    color: customAlert.type === 'error' ? '#dc3545' : 
                           customAlert.type === 'success' ? '#28a745' : 
                           customAlert.type === 'warning' ? '#ffc107' : 
                           '#17a2b8'
                  }}
                ></i>
              </div>
              <div className="flex-grow-1">
                <h6 
                  className={`mb-1 fw-bold`}
                  style={{ fontSize: '16px', 
                           color: customAlert.type === 'error' ? '#dc3545' : 
                                  customAlert.type === 'success' ? '#28a745' : 
                                  customAlert.type === 'warning' ? '#ffc107' : 
                                  '#17a2b8'
                         }}
                >
                  {customAlert.type === 'error' ? 'Error' : 
                   customAlert.type === 'success' ? 'Success' : 
                   customAlert.type === 'warning' ? 'Warning' : 
                   'Information'}
                </h6>
                <p className="mb-0 text-dark" style={{ fontSize: '14px', lineHeight: '1.4' }}>
                  {customAlert.message}
                </p>
              </div>
              <button
                type="button"
                className="btn-close ms-2"
                onClick={hideAlert}
                aria-label="Close"
                style={{
                  opacity: 0.8,
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              ></button>
            </div>
            <div 
              className="progress" 
              style={{ height: '4px', borderRadius: '0', backgroundColor: 'rgba(0,0,0,0.05)' }}
            >
              <div 
                className={`progress-bar ${
                  customAlert.type === 'error' ? 'bg-danger' : 
                  customAlert.type === 'success' ? 'bg-success' : 
                  customAlert.type === 'warning' ? 'bg-warning' : 
                  'bg-info'
                }`}
                role="progressbar" 
                style={{ width: '100%', animation: `progressBar ${customAlert.type === 'success' ? 4 : 5}s linear forwards` }} 
                aria-valuenow="100" 
                aria-valuemin="0" 
                aria-valuemax="100"
              ></div>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%) scale(0.9);
            opacity: 0;
          }
          to {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
        }
        @keyframes progressBar {
          from { width: 100%; }
          to { width: 0%; }
        }
        .nav-tabs::-webkit-scrollbar {
          height: 6px;
        }
        .nav-tabs::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        .nav-tabs::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 10px;
        }
        .nav-tabs::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
      `}</style>

      <Sidebar 
        sidebarCollapsed={sidebarCollapsed} 
        setSidebarCollapsed={setSidebarCollapsed} 
        toggleSidebar={toggleSidebar}
      />

      <main className="nxl-container" style={{ paddingTop: '0' }}>
        <div className="nxl-content" style={{ paddingTop: '0' }}>
          <div className="page-header">
            <div className="page-header-left d-flex align-items-center">
              <div className="nxl-navigation-toggle me-3">
                <a href="javascript:void(0);" id="menu-mini-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'none' : 'block' }}>
                  <i className="feather-align-left"></i>
                </a>
                <a href="javascript:void(0);" id="menu-expend-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'block' : 'none' }}>
                  <i className="feather-arrow-right"></i>
                </a>
              </div>
              <div className="page-header-title">
                <h5 className="m-b-10">Unreleased</h5>
              </div>
              <ul className="breadcrumb">
                <li className="breadcrumb-item"><a href="/investor-dashboard">Home</a></li>
                <li className="breadcrumb-item">Unreleased</li>
              </ul>
            </div>
            <div className="page-header-right ms-auto d-flex align-items-center gap-3">
              {/* Search Bar */}
              <div className="position-relative" style={{ width: '200px' }}>
                <i className="feather-search position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '14px', pointerEvents: 'none' }}></i>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search project"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    paddingLeft: '32px',
                    paddingRight: searchTerm ? '28px' : '12px',
                    fontSize: '12px',
                    borderColor: '#dee2e6',
                    borderRadius: '6px',
                    height: '32px'
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="btn-close position-absolute"
                    onClick={() => setSearchTerm('')}
                    style={{
                      right: '6px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '10px',
                      opacity: 0.6,
                      width: '16px',
                      height: '16px'
                    }}
                    aria-label="Clear search"
                  ></button>
                )}
              </div>
              {loginData && (
                <div className="d-flex align-items-center gap-2">
                  <i className="feather-user text-primary"></i>
                  <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                    Welcome, {(() => {
                      if (participants.length > 0 && loginData.id) {
                        const userParticipant = participants.find(p => p.participant_id == loginData.id || p.id == loginData.id);
                        return userParticipant?.name || loginData.name || 'User';
                      }
                      return loginData.name || 'User';
                    })()}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="container-fluid py-4">
            {/* Project Navigation Tabs */}
            {projects.length > 0 && (
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-0">
                  <ul className="nav nav-tabs border-0" style={{ 
                    padding: '0.5rem 1rem',
                    borderBottom: '1px solid #dee2e6',
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: '0.25rem',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e0 transparent'
                  }}>
                    {projects.map(project => {
                      const isVisible = !project.is_special || 
                                       (project.special_participants && project.special_participants.split(',').includes(loginData?.id + '')) || 
                                       loginData?.role == 1;
                      
                      if (!isVisible) return null;
                      
                      const isActive = selectedProject?.id === project.id;
                      
                      return (
                        <li key={project.id} className="nav-item" style={{ flexShrink: 0 }}>
                          <button
                            className={`nav-link ${isActive ? 'active' : ''}`}
                            onClick={() => populateCommitment(project)}
                            style={{ 
                              border: 'none',
                              background: isActive ? 'rgba(13, 110, 253, 0.1)' : 'none',
                              cursor: 'pointer',
                              padding: '0.625rem 1.25rem',
                              color: isActive ? '#0d6efd' : '#495057',
                              fontWeight: '600',
                              fontSize: '13px',
                              borderRadius: '0',
                              borderBottom: isActive ? '3px solid #0d6efd' : '3px solid transparent',
                              transition: 'all 0.3s ease',
                              position: 'relative',
                              whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.color = '#0d6efd';
                                e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.05)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.color = '#495057';
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            {project.name}
                            {(project.reco_done || project.recommendation_done) && (
                              <span 
                                style={{
                                  position: 'absolute',
                                  top: '4px',
                                  right: '4px',
                                  width: '8px',
                                  height: '8px',
                                  backgroundColor: '#28a745',
                                  borderRadius: '50%',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                }}
                              ></span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}

            {/* Stats Cards */}
            {selectedProject && !isSpecialProject && (
              <div className="row mb-4">
                <div className="col-sm-6 col-lg-3 mb-3">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: '#e3f2fd' }}>
                          <i className="feather-dollar-sign text-primary" style={{ fontSize: '20px' }}></i>
                        </div>
                        <div>
                          <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Total Allocation</div>
                          <div className="fw-bold text-dark" style={{ fontSize: '18px' }}>
                            {stats.totalAllocation.toLocaleString()} {selectedProject.allocation_currency || 'USDT'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3 mb-3">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: '#fff3e0' }}>
                          <i className="feather-users text-warning" style={{ fontSize: '20px' }}></i>
                        </div>
                        <div>
                          <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Participants</div>
                          <div className="fw-bold text-warning" style={{ fontSize: '18px' }}>
                            {stats.participants}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3 mb-3">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: '#ffebee' }}>
                          <i className="feather-trending-up text-danger" style={{ fontSize: '20px' }}></i>
                        </div>
                        <div>
                          <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Allocation Used</div>
                          <div className="fw-bold text-danger" style={{ fontSize: '18px' }}>
                            {stats.totalCommitment.toLocaleString()} {selectedProject.allocation_currency || 'USDT'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-3 mb-3">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body">
                      <div className="d-flex align-items-center gap-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: '#e8f5e9' }}>
                          <i className="feather-check-circle text-success" style={{ fontSize: '20px' }}></i>
                        </div>
                        <div>
                          <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Allocation Available</div>
                          <div className="fw-bold text-success" style={{ fontSize: '18px' }}>
                            {stats.allocationAvailable.toLocaleString()} {selectedProject.allocation_currency || 'USDT'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {selectedProject && selectedProject.name && (
              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                  <div className="d-flex gap-2 flex-wrap">
                    {loginData?.role == 1 && (
                      <button 
                        type="button" 
                        className="btn btn-warning btn-sm"
                        onClick={() => handleViewProject(selectedProject)}
                        disabled={isSpecialProject}
                      >
                        <i className="feather-eye me-1"></i>View {selectedProject.name}
                      </button>
                    )}
                    {(loginData?.role == 1 || loginData?.role == 2) && !selectedProject.vesting_done && (
                      <button 
                        type="button" 
                        className="btn btn-primary btn-sm"
                        onClick={() => setAddVesting(true)}
                      >
                        <i className="feather-plus me-1"></i>Add Vesting
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Main Content Card */}
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-bold">Allocation Details</h5>
              </div>
              <div className="card-body">
                {!addVesting ? (
                  <div className="table-responsive" style={{ overflowX: 'auto', maxWidth: '100%' }}>
                    <table className="table table-hover mb-0" style={{ width: '100%', tableLayout: 'fixed' }}>
                      <thead style={{ backgroundColor: '#f8f9fa' }}>
                        <tr>
                          <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '5%' }}>S. No.</th>
                          <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '12%' }}>Name</th>
                          <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '8%' }}>Currency</th>
                          <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '10%' }}>Amount</th>
                          <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '10%' }}>Alloc Amt</th>
                          <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '8%' }}>Mode</th>
                          <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '20%' }}>Transaction Hash</th>
                          <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '15%' }}>Comment</th>
                          <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '12%' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {commitments.map((commit, index) => {
                          if (commit.participant_id != loginData?.id && loginData?.role != 1) return null;
                          
                          return (
                            <tr key={commit.id || index} className="border-bottom">
                              <td className="px-2 py-2 text-center">
                                <span className="fw-semibold text-dark fs-12">{index + 1}</span>
                              </td>
                              <td className="px-2 py-2 text-center">
                                <span className="fw-medium text-dark fs-12" style={{ wordBreak: 'break-word' }}>{commit.participant_name}</span>
                              </td>
                              <td className="px-2 py-2 text-center">
                                <span className="fw-semibold text-dark fs-12">{selectedProject?.allocation_currency}</span>
                              </td>
                              <td className="px-2 py-2 text-center">
                                <span className="fw-semibold text-dark fs-12">{commit.amount}</span>
                              </td>
                              <td className="px-2 py-2 text-center">
                                <span className="fw-semibold text-dark fs-12">{commit.allocation_amount}</span>
                              </td>
                              <td className="px-2 py-2 text-center">
                                <span className="fw-medium text-dark fs-12">{commit.mode_payment}</span>
                              </td>
                              <td className="px-2 py-2 text-center">
                                {commit.transaction_hash ? (
                                  commit.transaction_hash.includes('https') ? (
                                    <a 
                                      href={commit.transaction_hash} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-primary"
                                      style={{ fontSize: '11px', wordBreak: 'break-all', display: 'block' }}
                                      title={commit.transaction_hash}
                                    >
                                      {commit.transaction_hash.length > 25 ? `${commit.transaction_hash.substring(0, 25)}...` : commit.transaction_hash}
                                    </a>
                                  ) : (
                                    <span 
                                      className="text-primary"
                                      style={{ fontSize: '11px', cursor: 'pointer', wordBreak: 'break-all', display: 'block' }}
                                      onClick={() => {
                                        navigator.clipboard.writeText(commit.transaction_hash);
                                        showAlert('Transaction hash copied to clipboard', 'success');
                                      }}
                                      title={commit.transaction_hash}
                                    >
                                      {commit.transaction_hash.length > 25 ? `${commit.transaction_hash.substring(0, 25)}...` : commit.transaction_hash}
                                    </span>
                                  )
                                ) : (
                                  <span className="text-muted fs-12">-</span>
                                )}
                              </td>
                              <td className="px-2 py-2 text-center">
                                <span className="fw-medium text-dark fs-12" style={{ wordBreak: 'break-word' }} title={commit.comment || ''}>{commit.comment ? (commit.comment.length > 20 ? `${commit.comment.substring(0, 20)}...` : commit.comment) : '-'}</span>
                              </td>
                              <td className="px-2 py-2 text-center">
                                {commit.is_verified == null ? (
                                  <div className="d-flex gap-1 justify-content-center flex-wrap">
                                    {commit.transaction_hash && (loginData?.id == 1 || loginData?.role == 4) && (
                                      <>
                                        <button 
                                          type="button" 
                                          className="btn btn-success btn-sm" 
                                          title="Approve" 
                                          onClick={() => approveTx(1, commit.id)}
                                          style={{ padding: '2px 6px', fontSize: '11px' }}
                                        >
                                          <i className="feather-check" style={{ fontSize: '12px' }}></i>
                                        </button>
                                        <button 
                                          type="button" 
                                          className="btn btn-danger btn-sm" 
                                          title="Reject" 
                                          onClick={() => approveTx(0, commit.id)}
                                          style={{ padding: '2px 6px', fontSize: '11px' }}
                                        >
                                          <i className="feather-x" style={{ fontSize: '12px' }}></i>
                                        </button>
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <span className={`badge ${commit.is_verified == 1 ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '10px' }}>
                                    {commit.is_verified == 1 ? 'Verified' : 'Rejected'}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {commitments.length === 0 && (
                          <tr>
                            <td colSpan="9" className="text-center py-4 text-muted">
                              <i className="feather-inbox fs-1 mb-2 d-block"></i>
                              No allocation found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="row">
                    <div className="col-12">
                      <h5 className="mb-3"><i className="feather-edit me-2"></i>Vesting Form</h5>
                      <form>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Price Per Token <span className="text-danger">*</span></label>
                            <div className="input-group">
                              <span className="input-group-text"><i className="feather-dollar-sign"></i></span>
                              <input 
                                type="text" 
                                className="form-control" 
                                placeholder="0" 
                                value={vestingForm.price_per_token || ''} 
                                onChange={(e) => setVestingForm({ ...vestingForm, price_per_token: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Cliff Period (Months) <span className="text-danger">*</span></label>
                            <input 
                              type="text" 
                              className="form-control" 
                              placeholder="Enter Cliff Period"
                              value={vestingForm.cliff_period || 0} 
                              onChange={(e) => setVestingForm({ ...vestingForm, cliff_period: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Vesting Type <span className="text-danger">*</span></label>
                            <select 
                              className="form-select" 
                              value={vestingForm.vesting_type || 'Monthly'} 
                              onChange={(e) => setVestingForm({ ...vestingForm, vesting_type: e.target.value })}
                            >
                              <option value="Monthly">Monthly</option>
                              <option value="Quarterly">Quarterly</option>
                              <option value="Linear">Linear</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Invested Round</label>
                            <select 
                              className="form-select" 
                              value={vestingForm.project_round || 'Strategic'} 
                              onChange={(e) => setVestingForm({ ...vestingForm, project_round: e.target.value })}
                            >
                              <option value="Strategic">Strategic</option>
                              <option value="Seed">Seed</option>
                              <option value="Pre">Pre</option>
                              <option value="Private">Private</option>
                              <option value="Private 1">Private 1</option>
                              <option value="Private 2">Private 2</option>
                              <option value="Private A">Private A</option>
                              <option value="Private B">Private B</option>
                              <option value="Mixed">Mixed</option>
                            </select>
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">TGE Release % <span className="text-danger">*</span></label>
                            <div className="input-group">
                              <span className="input-group-text"><i className="feather-percent"></i></span>
                              <input 
                                type="text" 
                                className="form-control" 
                                placeholder="0%" 
                                value={vestingForm.tge_release || ''} 
                                onChange={(e) => setVestingForm({ ...vestingForm, tge_release: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">TGE Date & Time</label>
                            <input 
                              type="datetime-local" 
                              className="form-control" 
                              value={vestingForm.tge_date || ''} 
                              onChange={(e) => setVestingForm({ ...vestingForm, tge_date: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Frequency (Months/Quarters) <span className="text-danger">*</span></label>
                            <input 
                              type="text" 
                              className="form-control" 
                              placeholder="0" 
                              value={vestingForm.no_of_repitition || ''} 
                              onChange={(e) => setVestingForm({ ...vestingForm, no_of_repitition: e.target.value })}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-semibold">Release Per Time (Post TGE)</label>
                            <div className="input-group">
                              <span className="input-group-text"><i className="feather-percent"></i></span>
                              <input 
                                type="text" 
                                className="form-control" 
                                placeholder="0%" 
                                value={vestingForm.release_per_time || ''} 
                                onChange={(e) => setVestingForm({ ...vestingForm, release_per_time: e.target.value })}
                              />
                            </div>
                          </div>
                        </div>
                        <div className="d-flex gap-2 justify-content-end mt-3 pt-3 border-top">
                          <button
                            type="button"
                            className="btn btn-light btn-sm"
                            onClick={() => setAddVesting(false)}
                          >
                            <i className="feather-x me-1"></i>Cancel
                          </button>
                          {!selectedProject?.vesting_done && (
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={saveVesting}
                            >
                              <i className="feather-save me-1"></i>Save
                            </button>
                          )}
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

      {/* View Project Modal - Similar to active.js */}
      {showAddProjectModal && (
        <>
          <div
            className="position-fixed"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1040,
              animation: 'fadeIn 0.3s ease'
            }}
            onClick={handleCloseAddProjectModal}
          />
          
          <div
            className="position-fixed bg-white shadow-lg"
            style={{
              top: 0,
              right: 0,
              bottom: 0,
              width: '600px',
              maxWidth: '90vw',
              zIndex: 1050,
              overflowY: 'auto',
              animation: 'slideInRight 0.3s ease',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div
              className="border-bottom bg-white"
              style={{
                padding: '1.5rem',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-bold">
                  <i className="feather-eye me-2 text-primary"></i>
                  View Project
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseAddProjectModal}
                  aria-label="Close"
                ></button>
              </div>
            </div>

            <div style={{ padding: '1.5rem', flex: 1 }}>
              <form onSubmit={(e) => { e.preventDefault(); }}>
                <div className="row">
                  <div className="col-12 mb-3">
                    <label className="form-label fw-semibold mb-2 text-dark fs-13">
                      <i className="feather-file-text me-2 text-primary fs-12"></i>
                      Project Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={projectForm.projectName}
                      disabled
                      style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: '#f8f9fa' }}
                    />
                  </div>
                  {/* Add other fields similar to active.js view modal */}
                </div>
              </form>
            </div>

            <div
              className="border-top bg-white"
              style={{
                padding: '1.5rem',
                position: 'sticky',
                bottom: 0,
                zIndex: 10,
                boxShadow: '0 -2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-light btn-sm"
                  onClick={handleCloseAddProjectModal}
                  style={{
                    borderRadius: '8px',
                    padding: '8px 20px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  <i className="feather-x me-1"></i>Close
                </button>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideInRight {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
          `}</style>
        </>
      )}
        </div>
      </main>
    </div>
  );
};

export default UnreleasedProjects;

